import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const getPreferredLanguage = () => {
  if (typeof window === "undefined") return "en";
  return localStorage.getItem("hookora_language") || "en";
};

const notifyStudioUsageUpdated = () => {
  const value = String(Date.now());
  localStorage.setItem(STUDIO_USAGE_REFRESH_KEY, value);
  window.dispatchEvent(new CustomEvent("hookora:studio-usage-updated"));
};

const formatStyleLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getScoreTone = (score) => {
  const safe = Number(score) || 0;
  if (safe >= 85) {
    return {
      bar: "#16a34a",
      pillBg: "rgba(22,163,74,0.14)",
      pillText: "#16a34a",
    };
  }
  if (safe >= 70) {
    return {
      bar: "#2563eb",
      pillBg: "rgba(37,99,235,0.14)",
      pillText: "#2563eb",
    };
  }
  return {
    bar: "#f59e0b",
    pillBg: "rgba(245,158,11,0.16)",
    pillText: "#b45309",
  };
};

const CaptionTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [tone, setTone] = useState("engaging");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [offer, setOffer] = useState("");
  const [painPoint, setPainPoint] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [angle, setAngle] = useState("");
  const [callToAction, setCallToAction] = useState("");
  const [keywords, setKeywords] = useState("");
  const [brandVoice, setBrandVoice] = useState("");
  const [avoidPhrases, setAvoidPhrases] = useState("");
  const [extraContext, setExtraContext] = useState("");

  const [captions, setCaptions] = useState([]);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncTheme = () => setTheme(localStorage.getItem(THEME_KEY) || "light");
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.22)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.34)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      trackBg: isDark ? "#1e293b" : "#e5e7eb",
      panelBg: isDark ? "rgba(30,41,59,0.55)" : "#f8fafc",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      dangerBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      dangerText: isDark ? "#fecaca" : "#b91c1c",
      label: isDark ? "#e2e8f0" : "#111827",
      heroBg: isDark
        ? "linear-gradient(135deg, rgba(30,41,59,0.84), rgba(15,23,42,0.96))"
        : "linear-gradient(135deg, #f8fafc, #ffffff)",
      heroBorder: isDark ? "rgba(59,130,246,0.24)" : "#bfdbfe",
      heroBadgeBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      heroBadgeText: isDark ? "#bfdbfe" : "#1d4ed8",
      heroBadgeBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      shadow: isDark
        ? "0 16px 34px rgba(2,6,23,0.34)"
        : "0 12px 28px rgba(15,23,42,0.06)",
      shadowSoft: isDark
        ? "0 10px 24px rgba(2,6,23,0.22)"
        : "0 6px 18px rgba(15,23,42,0.05)",
      chipBg: isDark ? "rgba(30,41,59,0.9)" : "#f1f5f9",
      chipText: isDark ? "#cbd5e1" : "#475569",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    outline: "none",
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText,
    fontSize: "14px",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "86px",
    resize: "vertical",
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "6px",
    color: colors.label,
    fontSize: "14px",
    fontWeight: 600,
  };

  const clearResults = () => {
    setCaptions([]);
    setCopiedId(null);
    setError("");
  };

  const resetForm = () => {
    setSharedTopic("");
    setSharedPlatform("Instagram");
    setTone("engaging");
    setAudience("");
    setGoal("");
    setOffer("");
    setPainPoint("");
    setDesiredOutcome("");
    setAngle("");
    setCallToAction("");
    setKeywords("");
    setBrandVoice("");
    setAvoidPhrases("");
    setExtraContext("");
    setCaptions([]);
    setCopiedId(null);
    setError("");
  };

  const generateCaptions = async () => {
    if (!sharedTopic.trim()) {
      setError(t("captionTool.errors.topicRequired", "Please enter a topic first."));
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await API.post("/studio/captions", {
        topic: sharedTopic.trim(),
        platform: sharedPlatform,
        tone,
        audience,
        goal,
        offer,
        pain_point: painPoint,
        desired_outcome: desiredOutcome,
        angle,
        call_to_action: callToAction,
        keywords,
        brand_voice: brandVoice,
        avoid_phrases: avoidPhrases,
        extra_context: extraContext,
        language: getPreferredLanguage(),
      });

      setCaptions(Array.isArray(response?.data?.captions) ? response.data.captions : []);
      setCopiedId(null);
      notifyStudioUsageUpdated();
    } catch (err) {
      console.error("Failed to generate captions:", err);
      setError(err?.response?.data?.detail || t("captionTool.errors.generateFailed", "Failed to generate captions."));
    } finally {
      setLoading(false);
    }
  };

  const copyCaption = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy caption:", err);
    }
  };

  const renderScoreBar = (score) => {
    const safe = Number(score) || 0;
    const toneColors = getScoreTone(safe);

    return (
      <div
        style={{
          marginTop: "10px",
          height: "10px",
          borderRadius: "999px",
          background: colors.trackBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, safe))}%`,
            height: "100%",
            borderRadius: "999px",
            background: toneColors.bar,
          }}
        />
      </div>
    );
  };

  return (
    <MotionSurface>
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          border: `1px solid ${colors.heroBorder}`,
          borderRadius: "20px",
          padding: "18px",
          background: colors.heroBg,
          boxShadow: colors.shadow,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 10px",
            borderRadius: "999px",
            background: colors.heroBadgeBg,
            color: colors.heroBadgeText,
            border: `1px solid ${colors.heroBadgeBorder}`,
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.03em",
            marginBottom: "12px",
          }}
        >
          {t("captionTool.hero.badge", "Social writing tool")}
        </div>

        <div style={{ color: colors.text, fontSize: "24px", fontWeight: 800, marginBottom: "8px" }}>
          {t("captionTool.hero.title", "Captions")}
        </div>

        <div style={{ color: colors.textSoft, lineHeight: "1.7", maxWidth: "880px" }}>
          {t(
            "captionTool.hero.description",
            "Use this tool to generate ready-to-post captions for social content. Add platform, tone, CTA direction, and extra context so Hookora can create stronger, more relevant caption options for your post or video."
          )}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "18px",
          padding: "18px",
          background: colors.cardBg,
          boxShadow: colors.shadowSoft,
        }}
      >
        <div style={{ display: "grid", gap: "14px" }}>
          <div>
            <label style={labelStyle}>
              {t("captionTool.fields.topic.label", "Topic")}
              <FieldHint text={t("captionTool.fields.topic.hint", "The main subject of the caption. Be clear about what the post or video is about.")} />
            </label>
            <textarea
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t("captionTool.fields.topic.placeholder", "What is the caption about?")}
              style={textareaStyle}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <label style={labelStyle}>
                {t("captionTool.fields.platform.label", "Platform")}
                <FieldHint text={t("captionTool.fields.platform.hint", "Choose where the caption will be posted. Platform affects tone, pacing, and CTA style.")} />
              </label>
              <select
                value={sharedPlatform}
                onChange={(e) => setSharedPlatform(e.target.value)}
                style={inputStyle}
              >
                {[
                  "Instagram",
                  "Instagram Reels",
                  "TikTok",
                  "X",
                  "LinkedIn",
                  "Facebook",
                  "YouTube Shorts",
                ].map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>
                {t("captionTool.fields.tone.label", "Tone")}
                <FieldHint text={t("captionTool.fields.tone.hint", "How the caption should sound. Example: engaging, bold, playful, educational, premium, direct.")} />
              </label>
              <input
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                placeholder={t("captionTool.fields.tone.placeholder", "Tone")}
                style={inputStyle}
              />
            </div>
          </div>

          <div
            style={{
              padding: "14px",
              borderRadius: "14px",
              background: colors.panelBg,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontWeight: 700, color: colors.text, marginBottom: "12px" }}>
              {t("captionTool.contextSectionTitle", "Caption Context")}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>
                  {t("captionTool.fields.audience.label", "Audience")}
                  <FieldHint text={t("captionTool.fields.audience.hint", "Who the caption is for. Example: skincare brands, creators, startup founders, Nigerian students.")} />
                </label>
                <input
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder={t("captionTool.fields.audience.placeholder", "Audience")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("captionTool.fields.goal.label", "Goal")}
                  <FieldHint text={t("captionTool.fields.goal.hint", "What you want the caption to achieve. Example: more saves, comments, clicks, signups, or shares.")} />
                </label>
                <input
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder={t("captionTool.fields.goal.placeholder", "Goal")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("captionTool.fields.offer.label", "Offer")}
                  <FieldHint text={t("captionTool.fields.offer.hint", "The product, service, value proposition, freebie, or message behind the content.")} />
                </label>
                <input
                  value={offer}
                  onChange={(e) => setOffer(e.target.value)}
                  placeholder={t("captionTool.fields.offer.placeholder", "Offer or product")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("captionTool.fields.painPoint.label", "Pain Point")}
                  <FieldHint text={t("captionTool.fields.painPoint.hint", "The problem or frustration your audience is dealing with.")} />
                </label>
                <input
                  value={painPoint}
                  onChange={(e) => setPainPoint(e.target.value)}
                  placeholder={t("captionTool.fields.painPoint.placeholder", "Pain point")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("captionTool.fields.desiredOutcome.label", "Desired Outcome")}
                  <FieldHint text={t("captionTool.fields.desiredOutcome.hint", "The result your audience wants. Example: more engagement, more leads, stronger content, better sales.")} />
                </label>
                <input
                  value={desiredOutcome}
                  onChange={(e) => setDesiredOutcome(e.target.value)}
                  placeholder={t("captionTool.fields.desiredOutcome.placeholder", "Desired outcome")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("captionTool.fields.angle.label", "Angle")}
                  <FieldHint text={t("captionTool.fields.angle.hint", "The perspective or framing. Example: beginner-friendly, urgent, story-led, myth-busting, premium.")} />
                </label>
                <input
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  placeholder={t("captionTool.fields.angle.placeholder", "Angle")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("captionTool.fields.callToAction.label", "CTA")}
                  <FieldHint text={t("captionTool.fields.callToAction.hint", "The action you want people to take. Example: comment, share, save, follow, click the link.")} />
                </label>
                <input
                  value={callToAction}
                  onChange={(e) => setCallToAction(e.target.value)}
                  placeholder={t("captionTool.fields.callToAction.placeholder", "CTA")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("captionTool.fields.keywords.label", "Keywords")}
                  <FieldHint text={t("captionTool.fields.keywords.hint", "Important search or topic words to include naturally. Separate them with commas.")} />
                </label>
                <input
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder={t("captionTool.fields.keywords.placeholder", "Keywords")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("captionTool.fields.brandVoice.label", "Brand Voice")}
                  <FieldHint text={t("captionTool.fields.brandVoice.hint", "How the caption should feel. Example: direct, witty, expert, playful, premium.")} />
                </label>
                <input
                  value={brandVoice}
                  onChange={(e) => setBrandVoice(e.target.value)}
                  placeholder={t("captionTool.fields.brandVoice.placeholder", "Brand voice")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("captionTool.fields.avoidPhrases.label", "Avoid Phrases")}
                  <FieldHint text={t("captionTool.fields.avoidPhrases.hint", "Repeated words, clichés, or phrases you do not want in the caption.")} />
                </label>
                <input
                  value={avoidPhrases}
                  onChange={(e) => setAvoidPhrases(e.target.value)}
                  placeholder={t("captionTool.fields.avoidPhrases.placeholder", "Avoid phrases")}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>
                {t("captionTool.fields.extraContext.label", "Extra Context")}
                <FieldHint text={t("captionTool.fields.extraContext.hint", "Add examples, product details, campaign notes, proof, or extra background to improve the output.")} />
              </label>
              <textarea
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder={t("captionTool.fields.extraContext.placeholder", "Extra context, campaign note, proof point, or creator angle")}
                style={textareaStyle}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "12px",
                background: colors.dangerBg,
                color: colors.dangerText,
              }}
            >
              {error}
            </div>
          )}

          <ActionRow>
            <AIActionButton isDark={isDark}
              onClick={generateCaptions}
              disabled={loading}
            >
              {loading
                ? t("captionTool.actions.generating", "Generating...")
                : t("captionTool.actions.generate", "Generate Captions")}
            </AIActionButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={clearResults}
            >
              {t("captionTool.actions.clearResults", "Clear Results")}
            </UtilityButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={resetForm}
            >
              {t("captionTool.actions.resetForm", "Reset Form")}
            </UtilityButton>
          </ActionRow>
        </div>
      </div>

      {!!captions.length && (
        <div
          style={{
            color: colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginTop: "-4px",
          }}
        >
          {t(
            "captionTool.resultsIntro",
            "These are ready-to-post caption options. Pick the one that best matches your platform, tone, and conversion goal."
          )}
        </div>
      )}

      <div style={{ display: "grid", gap: "14px" }}>
        {loading ? (
          <ToolResultSkeleton isDark={isDark} count={3} />
        ) : (
        captions.map((caption, index) => {
          const safeScore = Number(caption.score) || 0;
          const toneColors = getScoreTone(safeScore);

          return (
            <ResultCard
              key={`${caption.text}-${index}`}
              colors={colors}
              index={index}
            >
              <div style={{ whiteSpace: "pre-wrap", color: colors.text, lineHeight: "1.65" }}>
                {caption.text}
              </div>

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
                {caption.style && (
                  <span
                    style={{
                      background: colors.chipBg,
                      borderRadius: "999px",
                      padding: "6px 10px",
                      fontSize: "12px",
                      color: colors.chipText,
                      fontWeight: 600,
                    }}
                  >
                    {formatStyleLabel(caption.style)}
                  </span>
                )}

                <span
                  style={{
                    background: toneColors.pillBg,
                    borderRadius: "999px",
                    padding: "6px 10px",
                    fontSize: "12px",
                    color: toneColors.pillText,
                    fontWeight: 700,
                  }}
                >
                  {t("captionTool.labels.score", "Score")}: {safeScore}
                </span>
              </div>

              {renderScoreBar(safeScore)}

              <ActionRow style={{ marginTop: "14px" }}>
                <CopyButton
                  copied={copiedId === index}
                  onClick={() => copyCaption(caption.text, index)}
                >
                  {copiedId === index
                    ? t("captionTool.actions.copied", "Copied")
                    : t("captionTool.actions.copy", "Copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="captions"
                  title={`Caption • ${sharedTopic || t("captionTool.fallbackUntitledTopic", "Untitled Topic")}`}
                  topic={sharedTopic}
                  platform={sharedPlatform}
                  content={caption.text || ""}
                  meta={{
                    score: caption.score,
                    label: caption.label,
                    style: caption.style || "",
                    tone,
                  }}
                />
              </ActionRow>
            </ResultCard>
          );
        })
        )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default CaptionTool;